// Search Manifest — the single source of truth for search behaviour.
// See Architecture/Search_Architecture.md. Consumed by:
//   • the sitemap edge function          (what to list)
//   • the prerender pipeline (via the sitemap it lists → what to render)
//   • the client rendering layer          (which statics get runtime identity)
//   • _search_identity_audit.mjs          (what must hold true)
//
// PRINCIPLE: no search behaviour may be implemented outside this Manifest.
//
// Dependency-free ESM on purpose: it must import cleanly in Deno (edge function),
// Node (prerender + audit) and the Vite client bundle (main.jsx). No Deno/Node/DOM
// APIs, no external packages.
//
// Classes:
//   'critical'   — Search-Critical: must prerender · must self-canonical · must index
//   'commercial' — product decision (index iff acquisition)   (none declared yet)
//   'utility'    — never index, never prerender

export const SEARCH_CLASS = { CRITICAL: 'critical', COMMERCIAL: 'commercial', UTILITY: 'utility' };

const trimPath = (p) => ((p || '').split('?')[0].replace(/\/+$/, '') || '/');

// ── Static pages (fixed URLs, not data-driven) ───────────────────────────────
// url · changefreq · priority · class · inject
//   inject:true  → the client rendering layer must emit engine identity at runtime
//                  (page has no coded <SearchIdentity> of its own). Path-keyed, so it
//                  works even for pages whose internal route value differs from the URL
//                  (e.g. /editorial-standards renders through the Page Studio route).
//   inject:false/absent → the page already carries correct identity (its own component,
//                  or it is the reference control /careers). The injector must NOT touch
//                  these — it would risk changing a known-good title.
export const STATIC_PAGES = [
  { url: '/',                    changefreq: 'daily',   priority: '1.0', class: SEARCH_CLASS.CRITICAL },
  { url: '/all-destinations',    changefreq: 'weekly',  priority: '0.8', class: SEARCH_CLASS.CRITICAL },
  { url: '/lwd-edit',            changefreq: 'weekly',  priority: '0.8', class: SEARCH_CLASS.CRITICAL, inject: true }, // replaces /magazine (301'd)
  { url: '/publications',        changefreq: 'weekly',  priority: '0.8', class: SEARCH_CLASS.CRITICAL },
  { url: '/faq',                 changefreq: 'monthly', priority: '0.8', class: SEARCH_CLASS.CRITICAL },
  { url: '/about-us',            changefreq: 'monthly', priority: '0.7', class: SEARCH_CLASS.CRITICAL }, // self-wired (AboutLWD)
  { url: '/press',               changefreq: 'monthly', priority: '0.6', class: SEARCH_CLASS.CRITICAL },
  { url: '/careers',             changefreq: 'monthly', priority: '0.5', class: SEARCH_CLASS.CRITICAL }, // reference control — never inject
  // ── Authority cluster (Wave 1) — the LWD Standard pages ──
  { url: '/vision',              changefreq: 'monthly', priority: '0.6', class: SEARCH_CLASS.CRITICAL, inject: true },
  { url: '/the-lwd-standard',    changefreq: 'monthly', priority: '0.6', class: SEARCH_CLASS.CRITICAL, inject: true },
  { url: '/community',           changefreq: 'weekly',  priority: '0.6', class: SEARCH_CLASS.CRITICAL, inject: true },
  { url: '/artistry-awards',     changefreq: 'monthly', priority: '0.6', class: SEARCH_CLASS.CRITICAL, inject: true },
  // /editorial-standards is WITHHELD (Wave 1): it is a Page Studio page whose body loads
  // from localStorage/MOCK, so a clean prerender renders only a spinner — the prerender
  // gate skips it and it would be a soft-404 in raw HTML. Its identity resolves correctly,
  // but delivery does not. Add it back once it is a coded page (or its content is made
  // prerender-available). Listing it now would recreate the delivery gap Wave 1 closes.
];

// ── Global category hubs (/[category]) ───────────────────────────────────────
// `class` states INTENT: every category hub is Search-Critical by nature.
// `eligible` states MATURITY: whether the hub is exposed to search yet — a conscious
//   decision, NOT a function of the live count. It does not auto-flip when listings
//   grow; an owner flips it deliberately when inventory + search quality justify it.
// `inventory` is a recorded snapshot (2026-07-23), an annotation for that decision —
//   NOT a live number. Only `eligible !== false` hubs reach the sitemap.
// Hubs render through RegionCategoryPage, which carries its own <SearchIdentity> (no inject).
export const GLOBAL_CATEGORY_HUBS = [
  // Live (Wave 1) — highest-value Search-Critical hubs.
  { url: '/wedding-venues',   changefreq: 'weekly', priority: '0.85', class: SEARCH_CLASS.CRITICAL, inventory: 635, eligible: true },
  { url: '/wedding-planners', changefreq: 'weekly', priority: '0.85', class: SEARCH_CLASS.CRITICAL, inventory: 63,  eligible: true },
  { url: '/photographers',    changefreq: 'weekly', priority: '0.8',  class: SEARCH_CLASS.CRITICAL, inventory: 21,  eligible: true },
  // Proposed next tranche — enough inventory to justify an indexable hub; awaiting owner
  // sign-off. Flip eligible→true (one line) once approved, then verify raw HTML like Wave 1.
  { url: '/bridal-boutiques', changefreq: 'weekly', priority: '0.7',  class: SEARCH_CLASS.CRITICAL, inventory: 16,  eligible: false },
  { url: '/videographers',    changefreq: 'weekly', priority: '0.7',  class: SEARCH_CLASS.CRITICAL, inventory: 12,  eligible: false },
  { url: '/entertainment',    changefreq: 'weekly', priority: '0.7',  class: SEARCH_CLASS.CRITICAL, inventory: 12,  eligible: false },
  { url: '/jewellers',        changefreq: 'weekly', priority: '0.7',  class: SEARCH_CLASS.CRITICAL, inventory: 4,   eligible: false },
  // Zero-inventory categories are ineligible until they have listings (kept out to avoid
  // thin hubs): bridal-dresses, bridal-wear, guest-attire, accessories, hair-makeup,
  // health-beauty, florists, styling-decor, event-design, event-production, caterers,
  // wedding-cakes, celebrants, stationery, luxury-transport, gift-registry, wedding-vendors.
];

// ── Regional hubs (bare /country/region) — Wave 2A ───────────────────────────
// Curated, editorially-APPROVED region hubs (Tai, 2026-07-23). Eligibility is a QUALITY
// JUDGEMENT — destination search intent + enough substance to be an authority page — NOT a
// listing-count threshold; inventory is one input. Thin/zero-inventory regions are eligible
// by omission = false (they stay noindex, out of the sitemap). RegionPage renders these;
// identity is engine-owned. `kind:'region'` (explicit curated paths, expanded 1:1).
// HELD pending taxonomy: /italy/amalfi-coast (a Destination Area, not an administrative region
// — see the geo-taxonomy direction in Search_Rendering_Layer.md). Consolidation-excluded:
// /italy/rome, /morocco/marrakech (they 301 to their canonical).
export const REGIONAL_HUBS = [
  // Italy
  { url: '/italy/tuscany',            changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/campania',           changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/umbria',             changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/veneto',             changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  // /italy/puglia RELEASED 2026-07-30, after four months held. The recorded reason for the
  // hold ("RegionPage's dbListings fetch returns empty during prerender, page captured as a
  // spinner") was a misdiagnosis: /italy/puglia never reached RegionPage at all. main.jsx
  // routed it to PugliaPage, a Phase 3.1 demo gated on getRegionPageConfig(), whose body was
  // `return null` — so it could only ever render "Unable to load Puglia page configuration".
  // The hold treated a symptom of that dead route as a rendering-readiness problem, which is
  // why it survived every eligibility review. The demo route is now removed and the hub
  // renders through the generic RegionPage: h1, 5 sections, 991 body words, prerender-ready.
  // Substance was never the issue — 6 published listings and a published guide with 6 FAQs
  // put it ahead of 7 eligible hubs carrying 3 listings and 7 carrying no guide at all.
  { url: '/italy/puglia',             changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/lazio',              changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  // /italy/rome PROMOTED 2026-08-15, completing the region_slug migration of 2026-08-12.
  // That migration moved 12 listings from the legacy 'lazio' slug to 'rome' and added a
  // 301 from the old /italy/lazio/rome city hub, but never published the target: 'rome'
  // was in no hub list, so it was never prerendered, carried no self-canonical, and the
  // edge middleware served it 404 + noindex. The redirect therefore pointed a previously
  // indexed hub straight into a dead page — strictly worse than before the migration.
  // Rome is not being promoted on inventory. It clears the same bar the other entries
  // clear: 1,402 rendered words, a real h1, its own focusKeywords and intentSignals in
  // regions.js, and canonicalRoute '/italy/rome' already declaring this the canonical
  // shape. Verified 7/7 on a prerender pass before promotion, not assumed.
  { url: '/italy/rome',               changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/lombardy',           changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/italy/sicily',             changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  // England
  { url: '/england/london',           changefreq: 'weekly', priority: '0.8',  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/surrey',           changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/gloucestershire',  changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/berkshire',        changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/north-yorkshire',  changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/hertfordshire',    changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/somerset',         changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/england/cornwall',         changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  // Spain
  { url: '/spain/catalonia',          changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/spain/ibiza',              changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/spain/mallorca',           changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  // Middle East
  { url: '/uae/dubai',                changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
  { url: '/qatar/doha',               changefreq: 'weekly', priority: '0.75', class: SEARCH_CLASS.CRITICAL, eligible: true, kind: 'region' },
];

// ── Utility pages: direct-access, noindex (Project 3, Class 6, 2026-08-02) ──────
// Real, coded pages (ContactLWD, CmsPage, TaigenicPage, etc.) that were never in any
// route list at all — confirmed by a direct search of _prerender.mjs finding zero
// references to any of these 8 paths. Sibling static pages (/about-us, /press, /vision,
// /the-lwd-standard, /careers) work because they're in STATIC_PAGES (sitemap-listed,
// indexed); these 8 are legal/application pages that should return 200 to a direct
// visitor but must NOT be indexed or advertised in the sitemap. class: UTILITY already
// excludes them from sitemapStaticEntries()/eligibleSearchEntries(); utilityPaths()
// below is the extra list _prerender.mjs merges in (sitemap presence is not how these
// get rendered) and functions/_middleware.js reads to add the noindex header to real,
// successfully-rendered content — the one deliberate exception to "never intercept real
// content" elsewhere in that file.
export const UTILITY_PAGES = [
  // NOT "/contact" — that's a client-side alias (main.jsx SLUG_ALIASES, redirectToCanonical)
  // to /contact-us, not a real distinct route. Prerendering "/contact" captured homepage
  // shell-bleed because the client redirect fires mid-snapshot (confirmed live 2026-08-02).
  // /contact-us is the actual canonical, content-bearing page; /contact gets a real edge
  // 301 in public/_redirects instead, matching the existing /about → /about-us pattern.
  { url: "/contact-us",                    class: SEARCH_CLASS.UTILITY },
  { url: "/cookie-policy",                 class: SEARCH_CLASS.UTILITY },
  // Added 2026-08-07. Both render as real pages (main.jsx `page === "privacy-policy"` /
  // `"terms-and-conditions"`) but were absent here, so they were never prerendered, never
  // got a self-canonical, and the edge middleware served them as 404s — while SiteFooter
  // and FooterForCouples linked to them from EVERY page on the site. Their /privacy and
  // /terms aliases get edge 301s in public/_redirects, matching the /about and /contact
  // pattern; aliases are deliberately NOT prerendered (client redirect fires mid-snapshot).
  { url: "/privacy-policy",                class: SEARCH_CLASS.UTILITY },
  { url: "/terms-and-conditions",          class: SEARCH_CLASS.UTILITY },
  { url: "/apply-for-inclusion",           class: SEARCH_CLASS.UTILITY },
  { url: "/boutique-pricing-application",  class: SEARCH_CLASS.UTILITY },
  { url: "/vendor-pricing-application",    class: SEARCH_CLASS.UTILITY },
  { url: "/venue-pricing-application",     class: SEARCH_CLASS.UTILITY },
  { url: "/taigenic",                      class: SEARCH_CLASS.UTILITY },
  { url: "/wedding-vendors",               class: SEARCH_CLASS.UTILITY },
];
export function utilityPaths() {
  return UTILITY_PAGES.map((p) => p.url);
}

// ── Editorial category hubs (/lwd-edit/category/[cat]) — Project 3, 2026-08-02 ──
// Only `eligible: true` reaches the sitemap; only sitemap URLs are prerendered; only
// prerendered pages carry a self-canonical — and the edge middleware 404s any page
// without one. So an absent or held entry here IS a hard 404, not a soft omission.
//
// Promotion rule (unchanged): a hub earns `eligible: true` once it has real published
// content, verified against magazine_posts.category_slug — never assumed.
//
// 2026-08-07 recheck against production. The 2026-08-02 list had gone stale in two ways:
//   (a) FOUR categories with real content were absent from this file entirely, so they
//       could never be promoted and simply 404'd — stranding 19 published articles with
//       no reachable hub: editorial (11), perspectives (4), fashion (4). Promoted below.
//       travel (1) is deliberately still held: one article is a thin page, and shipping
//       thin pages is the mistake this manifest exists to prevent.
//   (b) FIVE held entries were phantoms — art-culture, entertaining, flowers,
//       restaurants and wine do not exist as rows in magazine_categories at all, so they
//       could never acquire content and would be held forever. Removed.
// Verified counts at time of change: our-venues 260, our-creatives 34, editorial 11,
// destinations 5, perspectives 4, fashion 4, travel 1, real-weddings 0.
export const EDITORIAL_CATEGORY_HUBS = [
  { url: "/lwd-edit/category/our-venues",   changefreq: "weekly", priority: "0.75", class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  { url: "/lwd-edit/category/our-creatives",changefreq: "weekly", priority: "0.7",  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  { url: "/lwd-edit/category/editorial",    changefreq: "weekly", priority: "0.7",  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  { url: "/lwd-edit/category/destinations", changefreq: "weekly", priority: "0.7",  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  { url: "/lwd-edit/category/perspectives", changefreq: "weekly", priority: "0.7",  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  { url: "/lwd-edit/category/fashion",      changefreq: "weekly", priority: "0.7",  class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "editorial-category" },
  // Held — real magazine_categories rows, but too little published content to justify a
  // hub yet. Flip eligible→true once each has real content, the same one-line promotion
  // REGIONAL_HUBS uses. Counts rechecked 2026-08-07.
  { url: "/lwd-edit/category/travel",       changefreq: "weekly", priority: "0.6",  class: SEARCH_CLASS.CRITICAL, eligible: false, kind: "editorial-category" },
  { url: "/lwd-edit/category/real-weddings",changefreq: "weekly", priority: "0.6",  class: SEARCH_CLASS.CRITICAL, eligible: false, kind: "editorial-category" },
  { url: "/lwd-edit/category/home-living",  changefreq: "weekly", priority: "0.6",  class: SEARCH_CLASS.CRITICAL, eligible: false, kind: "editorial-category" },
  { url: "/lwd-edit/category/hotels",       changefreq: "weekly", priority: "0.6",  class: SEARCH_CLASS.CRITICAL, eligible: false, kind: "editorial-category" },
];

// ── City hubs (bare /country/region/city) — Project 3, 2026-08-02 ───────────────
// Same shape as REGIONAL_HUBS one tier down: curated, editorially-approved, NOT a
// mechanical function of listing count. sitemap.ts's tierCity map builds
// /country/region/city/CATEGORY (4-segment) from real listing data, but there was no
// tier anywhere for the bare 3-segment city hub itself — confirmed missing by direct
// code read, not assumed. /italy/rome and /morocco/marrakech-safi/marrakech both
// already have live, populated 4-segment category pages (/wedding-venues,
// /wedding-planners) and an existing middleware alias-collapse (/italy/rome →
// /italy/lazio/rome) that was, until this fix, a redirect straight into a 404 — the
// same "unpublished routes must not be linked to" defect Class 3's /destinations/*
// redirects had, one tier deeper. Both cities clear the same bar REGIONAL_HUBS entries
// need (real, populated child pages), so both are eligible:true here.
export const CITY_HUBS = [
  // /italy/lazio/rome REMOVED 2026-08-15. It is no longer a page: the region_slug migration
  // made 'rome' a region rather than a city of 'lazio', so this URL now 301s to /italy/rome
  // (public/_redirects) and its replacement is published in REGIONAL_HUBS above. Leaving it
  // here kept a redirecting URL in the sitemap, which this file's own rule forbids, and the
  // 301 landed on an unpublished 404 — the two halves of the same defect.
  { url: "/morocco/marrakech-safi/marrakech", changefreq: "weekly", priority: "0.72", class: SEARCH_CLASS.CRITICAL, eligible: true, kind: "city" },
];

// Paths explicitly withheld from every search surface: aliases / consolidated URLs
// that redirect. Mirrors the edge 301s — the sitemap must never emit a URL that 301s.
export const SEARCH_EXCLUDED_PATHS = new Set([
  '/magazine',  // 301 → /lwd-edit
  '/the-edit',  // 301 → /lwd-edit
  '/about',     // alias → /about-us
]);

// ── Derived helpers ──────────────────────────────────────────────────────────

// Static + category-hub entries the sitemap should emit. Excludes utility, redirected
// aliases, and hubs not yet eligible for search (eligible === false). A missing
// `eligible` field means eligible (the authority statics carry no flag).
export function sitemapStaticEntries() {
  return [...STATIC_PAGES, ...GLOBAL_CATEGORY_HUBS, ...REGIONAL_HUBS, ...CITY_HUBS, ...EDITORIAL_CATEGORY_HUBS]
    .filter((p) => p.class !== SEARCH_CLASS.UTILITY && p.eligible !== false && !SEARCH_EXCLUDED_PATHS.has(p.url));
}

// Eligible Search-Critical routes, each tagged with HOW its routes are sourced (`kind`).
// This is the accessor the Search Intelligence layer (Phase 2.5) reads. Wave 1 is entirely
// `kind: 'fixed'`. Wave 2+ adds entries whose kind is 'geo' / 'category-derived' /
// 'db-derived' plus an expander that turns them into concrete routes — the assertion library
// and the CLI never change, only this expansion does. Deliberately NOT limited to statics:
// the name signals the eventual system spans listings, showcases, editorials and geo hubs.
export function eligibleSearchEntries() {
  return [...STATIC_PAGES, ...GLOBAL_CATEGORY_HUBS, ...REGIONAL_HUBS, ...CITY_HUBS, ...EDITORIAL_CATEGORY_HUBS]
    .filter((p) => p.class === SEARCH_CLASS.CRITICAL && p.eligible !== false && !SEARCH_EXCLUDED_PATHS.has(p.url))
    .map((p) => ({ route: p.url, kind: p.kind || 'fixed', class: p.class, isHome: p.url === '/' }));
}

// Redirected/consolidated aliases and how they must resolve — the Search Intelligence
// production check asserts these behave as intended and never appear in the sitemap.
export const SEARCH_ALIAS_EXPECTATIONS = [
  { path: '/magazine', expect: 'redirect', target: '/lwd-edit' },
  { path: '/the-edit', expect: 'redirect', target: '/lwd-edit' },
  { path: '/about',    expect: 'redirect', target: '/about-us' },   // 301 (was a client-side alias)
  { path: '/contact',  expect: 'redirect', target: '/contact-us' }, // 301 (was a client-side alias, Project 3 Class 6)
];

// Paths the client rendering layer must inject engine identity for (path-keyed).
export const INJECT_IDENTITY_PATHS = new Set(
  STATIC_PAGES.filter((p) => p.inject).map((p) => p.url),
);
export function needsRuntimeIdentity(path) {
  return INJECT_IDENTITY_PATHS.has(trimPath(path));
}

// A redirected/consolidated alias that must never appear on a search surface.
export function isSearchExcluded(path) {
  return SEARCH_EXCLUDED_PATHS.has(trimPath(path));
}

// Fixed-URL Search-Critical pages (statics + hubs). Data-driven Search-Critical
// pages (listings, showcases, geo/category hubs, editorial articles) are governed
// by their page-type contract, not this fixed list.
// Fixed Search-Critical pages that are LIVE (eligible). Ineligible hubs are Search-Critical
// by intent but not yet exposed, so the acceptance test must not require them to pass.
const CRITICAL_FIXED = new Set(
  [...STATIC_PAGES, ...GLOBAL_CATEGORY_HUBS, ...REGIONAL_HUBS, ...CITY_HUBS, ...EDITORIAL_CATEGORY_HUBS]
    .filter((p) => p.class === SEARCH_CLASS.CRITICAL && p.eligible !== false)
    .map((p) => p.url),
);
export function isSearchCriticalStatic(path) {
  return CRITICAL_FIXED.has(trimPath(path));
}

// ── Legacy /destinations/* redirects — ONE authoritative map (Project 3, 2026-08-02) ──
// Was two independently hand-maintained copies: public/_redirects (edge-level, fires on
// a fresh request) and main.jsx's DEST_MAP (client-level, fires on in-app navigation that
// never reaches the edge). They drifted — main.jsx had 14 slugs, _redirects had 21, and
// several targets on BOTH pointed at regions/cities that were never promoted into
// REGIONAL_HUBS, redirecting real traffic straight into a 404 (Project 3, Class 3/7).
// This is the single source now. Consumed by:
//   • main.jsx (client-side redirect for in-app navigation — imports this directly)
//   • _generate_redirects.mjs (writes the /destinations/* block of public/_redirects)
//   • _validate_destination_redirects.mjs (release-guard check: every non-safe target
//     must resolve against REGIONAL_HUBS / STATIC_PAGES / GLOBAL_CATEGORY_HUBS or be a
//     live bare-country page — never a region/city that hasn't been promoted)
// Unpromoted destinations redirect to the nearest published PARENT (bare country page),
// never to the dead region/city page itself — "unpublished routes must not be linked to."
export const LEGACY_DESTINATIONS = {
  // Promoted — safe to point directly at the region/category hub.
  tuscany:      "/italy/tuscany",
  puglia:       "/italy/puglia",
  sicily:       "/italy/sicily",
  london:       "/england/london",
  // Not modelled as LWD region hubs at all — nearest sensible published surface.
  santorini:    "/greece",
  provence:     "/france",
  marbella:     "/spain",
  algarve:      "/",
  lucerne:      "/",
  bali:         "/",
  // Unpromoted regions/cities (confirmed 404 2026-08-02, Project 3 Class 3/7) — redirect
  // to the nearest published parent, not the dead page. Revisit each once its region/city
  // is added to REGIONAL_HUBS through the normal editorial-promotion process.
  "lake-como":     "/italy",
  "amalfi-coast":  "/italy",
  sardinia:        "/italy",
  venice:          "/italy",
  milan:           "/italy",
  // rome repointed 2026-08-15: it is now a promoted region hub (REGIONAL_HUBS), so the
  // legacy /destinations/rome link can finally land on Rome itself rather than bouncing
  // visitors up to the country page. The "nearest published parent" fallback below still
  // governs every destination that is genuinely unpublished.
  rome:            "/italy/rome",
  cotswolds:       "/england",
  edinburgh:       "/scotland",
};
